type AnyOrder = Record<string, any>;
type Settings = Record<string, any>;

const money = (n: unknown) => `${Number(n || 0).toFixed(2)} EGP`;

function rows(order: AnyOrder): string {
  const items: AnyOrder[] = order.items || order.orderItems || [];
  return items
    .map(
      (it) => {
        const name = it.product_name || it.nameEn || it.name || it.nameAr || 'Item';
        const qty = it.quantity ?? 1;
        const price = it.unit_price ?? it.price ?? 0;
        const total = price * qty;
        return `<tr>
          <td>
            <div style="font-weight: 600;">${name}</div>
            ${it.color ? `<div style="font-size: 11px; color: #666;">Color: ${it.color_name || it.color}</div>` : ''}
            ${it.size ? `<div style="font-size: 11px; color: #666;">Size: ${it.size}</div>` : ''}
          </td>
          <td style="text-align:center">${qty}</td>
          <td style="text-align:end">${money(price)}</td>
          <td style="text-align:end; font-weight: 600;">${money(total)}</td>
        </tr>`;
      },
    )
    .join('');
}

export function buildInvoiceHtml(order: AnyOrder, settings: Settings = {}): string {
  const orderNum = order.order_number || order.orderNumber || order.id || '';
  const dateStr = new Date(order.created_at || order.createdAt || Date.now()).toLocaleString();
  
  return `<!doctype html><html><head><meta charset="utf-8" />
<title>Invoice ${orderNum}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 40px; color: #111; line-height: 1.4; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
  .logo { font-size: 26px; font-weight: bold; letter-spacing: 0.05em; color: #250f03; margin: 0; }
  .header-title { font-size: 11px; color: #666; font-weight: 600; text-transform: uppercase; margin-top: 4px; }
  .header-right { text-align: end; }
  .order-num { font-size: 14px; font-weight: bold; color: #111; }
  .order-date { font-size: 11px; color: #666; margin-top: 4px; }
  hr { border: 0; border-top: 1px solid #ddd; margin: 20px 0; }
  .section-title { font-size: 10px; font-weight: bold; color: #888; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 24px; }
  .grid-col { font-size: 13px; }
  .label-val { margin-bottom: 10px; }
  .label { font-size: 9px; font-weight: bold; color: #999; text-transform: uppercase; margin-bottom: 2px; }
  .value { font-size: 12px; color: #111; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
  th { background-color: #f4f4f4; text-align: start; text-transform: uppercase; font-size: 10px; font-weight: bold; color: #555; padding: 8px 12px; letter-spacing: 0.05em; }
  td { border-bottom: 1px solid #eee; padding: 10px 12px; vertical-align: top; }
  .totals-box { display: flex; justify-content: flex-end; margin-top: 24px; font-size: 13px; }
  .totals-table { width: 300px; }
  .totals-table td { border: none; padding: 4px 0; }
  .totals-table tr.grand-total td { font-size: 15px; font-weight: bold; color: #250f03; border-top: 1px solid #bbb; padding-top: 8px; margin-top: 4px; }
  .footer { text-align: center; margin-top: 60px; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 15px; }
</style></head><body>
  <div class="header">
    <div>
      <div class="logo">SEEN</div>
      <div class="header-title">INVOICE</div>
    </div>
    <div class="header-right">
      <div class="order-num">#${orderNum}</div>
      <div class="order-date">${dateStr}</div>
    </div>
  </div>
  <hr />
  
  <div class="section-title">Customer Information</div>
  <div class="grid">
    <div class="grid-col">
      <div class="label-val">
        <div class="label">Full Name</div>
        <div class="value">${order.customer_name || order.customer?.name || ''}</div>
      </div>
      <div class="label-val">
        <div class="label">City</div>
        <div class="value">${order.city || order.address?.city || ''}</div>
      </div>
    </div>
    <div class="grid-col">
      <div class="label-val">
        <div class="label">Phone</div>
        <div class="value">${order.customer_phone || order.customer?.phone || order.phone || ''}</div>
      </div>
      <div class="label-val">
        <div class="label">Address</div>
        <div class="value">${typeof order.address === 'object' ? (order.address?.address || order.address?.street || '') : (order.address || '')}</div>
      </div>
    </div>
  </div>

  ${(settings.sender_name || settings.sender_account_number) ? `
  <div class="section-title">Sender Information</div>
  <div class="grid">
    <div class="grid-col">
      <div class="label-val">
        <div class="label">Sender</div>
        <div class="value">${settings.sender_name}</div>
      </div>
    </div>
    <div class="grid-col">
      <div class="label-val">
        <div class="label">Account Number</div>
        <div class="value">${settings.sender_account_number}</div>
      </div>
    </div>
  </div>` : ''}

  <div class="section-title">Items</div>
  <table>
    <thead>
      <tr>
        <th>Item</th>
        <th style="text-align:center; width: 60px;">Qty</th>
        <th style="text-align:end; width: 100px;">Unit</th>
        <th style="text-align:end; width: 100px;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${rows(order)}
    </tbody>
  </table>

  <div class="totals-box">
    <table class="totals-table">
      <tr>
        <td style="color: #555;">Subtotal</td>
        <td style="text-align:end;">${money(order.subtotal)}</td>
      </tr>
      <tr>
        <td style="color: #555;">Shipping</td>
        <td style="text-align:end;">${money(order.shipping_price ?? order.shipping)}</td>
      </tr>
      ${(order.discount_amount || order.discount) && Number(order.discount_amount || order.discount) > 0 ? `
      <tr>
        <td style="color: #555;">Discount</td>
        <td style="text-align:end; color: #ba1a1a;">-${money(order.discount_amount || order.discount)}</td>
      </tr>` : ''}
      <tr class="grand-total">
        <td>Total</td>
        <td style="text-align:end;">${money(order.total)}</td>
      </tr>
    </table>
  </div>

  <div class="footer">
    Thank you for shopping with SEEN.
  </div>
</body></html>`;
}

export async function downloadOrderInvoice(order: AnyOrder, settings: Settings = {}): Promise<void> {
  const html = buildInvoiceHtml(order, settings);
  const win = window.open('', '_blank');
  if (!win) throw new Error('Popup blocked — allow popups to download the invoice');
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}
